import React, { useEffect, useRef, useState } from 'react';
import { X, Sparkles, Loader2, AlertTriangle, FileVideo, RefreshCw, Mic, MessageSquare, Check, Send, HelpCircle } from 'lucide-react';
import { regenApi } from '../lib/api';
import { TemplateLibrary } from './ui/TemplateLibrary';

type Source = { filename: string | null; url: string | null; thumbnail: string | null; label: string | null };
type Voice = { gender: string | null; age_band: string | null } | null;
type PreviewJob = {
  // Stable 0-based position from the backend preview; used to select which jobs to generate.
  // Falls back to array position when absent (older backend / resilience).
  index?: number;
  source: Source | null;
  script_mode: string;          // "verbatim" | "modify" | "reuse"
  script_preview: string;       // spoken line, or "[modify] …", "[script S3]", "[reuse clip transcript]"
  voice: Voice;
  state: string | null;
  captions: boolean;
  count: number;
  // Present only when this job is an avatar recast. Tells the admin whether ORBIT will
  // reuse a known library face ('library') or spin up a fresh one ('generate').
  recast?: { enabled: boolean; plan: 'library' | 'generate'; match: string | null };
  // Present only when the source clip has multiple people — one voice is used for all of them.
  multi_face?: { count: number; warning: boolean };
  // Which axis this variation differs along, and its 1-based position in the batch.
  // Absent on older backends — every read guards for that.
  variation_axis?: string;
  variation_index?: number;
  variation_total?: number;
};
type Unresolved = { filename: string | null; reason: string };
// Phase-1 brain read — the plain-language interpretation the conversational confirm (Phase 2) shows.
type Intent = {
  lane: string;
  lane_label: string;
  sources: string[];
  attachments_read: number;
  recast: boolean;
  script_mode: string;
  vertical: string | null;
  count: number | null;
  confidence: string;
  brain_answered?: boolean;
  source?: string;
  traits?: Record<string, string>;
};
type Interpretation = {
  jobs: PreviewJob[];
  vertical?: string;
  state?: string | null;
  unresolved: Unresolved[];
  // What the N variations differ by, and the axes the admin may switch to. Absent on older backends.
  variation_axis?: string;
  available_axes?: string[];
  // Phase 2: the brain's read + a one-line summary (absent on pre-Phase-1 backends).
  intent?: Intent;
  summary?: string;
};
// One turn of the draft ORBIT conversation (same shape as a studio_messages row).
type ConvMsg = { id: number | string; role: string; kind: string; text: string | null; data?: any; created_at?: string };

// Fallback axes when the backend doesn't enumerate them (but still supports the contract).
const AXES = ['character', 'script', 'hook', 'format'];

// script_mode → readable badge. Distinct colours so a misread mode is caught at a glance.
const MODE_BADGE: Record<string, string> = {
  verbatim: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  modify: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  reuse: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
};
const fmtAge = (a: string | null) =>
  a ? a.replace('plus', '+').replace(/^under/, 'under ').replace('-', '–') : '';

// Map the brain's read → one of the ORBIT flows we show explicitly (mirrors backend orbitFlow()).
// Keyed off intent.lane + recast + whether a source is bound — NOT lane_label (a recast vs. keep-face
// edit share the same lane_label, so we distinguish them here). Plain, non-technical wording.
type Flow = { key: string; label: string; blurb: string };
function flowFromIntent(intent?: Intent): Flow {
  const lane = intent?.lane;
  if (lane === 'recreate') return { key: 'recreate', label: 'Recreate winner', blurb: 'We\'ll make a fresh video with a brand-NEW person (AI-generated) speaking the SAME words as your attached video — same style/scene, new face. Costs more, takes longer.' };
  if (intent?.recast) return { key: 'avatar', label: 'New face', blurb: 'Same video and same words, but the person is a brand-NEW AI-generated face — not the one in your clip.' };
  if (lane === 'reference_image') return { key: 'reference', label: 'From your image', blurb: 'We\'ll use your attached IMAGE as the person and generate a video of them speaking your words.' };
  if ((intent?.sources || []).length) return { key: 'edit', label: 'Lightly edit', blurb: 'We\'ll use your attached video as-is (same person, same scene) and lip-sync a fresh clean voice of the same words onto it — plus captions. Nothing is re-generated.' };
  return { key: 'scratch', label: 'From scratch', blurb: 'We\'ll generate a brand-new video from your written description — no attached clip is used.' };
}
const CONF_BADGE: Record<string, string> = {
  high: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  low: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
};
// Minimal **bold** renderer for the conversation bubbles (the cards use markdown bold).
function renderBold(text: string): React.ReactNode {
  return String(text).split('**').map((seg, i) => (i % 2 ? <strong key={i}>{seg}</strong> : <span key={i}>{seg}</span>));
}

/** Source thumbnail with a graceful file-icon fallback (thumbnail may be null or dead). */
function SourceThumb({ src }: { src: Source | null }) {
  const [broke, setBroke] = useState(false);
  const url = src?.thumbnail || null;
  if (url && !broke) {
    return (
      <img src={url} alt="" onError={() => setBroke(true)}
        className="shrink-0 w-12 h-12 rounded-lg object-cover border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800" />
    );
  }
  return (
    <span className="shrink-0 w-12 h-12 rounded-lg bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300 flex items-center justify-center">
      <FileVideo className="w-5 h-5" />
    </span>
  );
}

/**
 * ORBIT — auto-fulfil a file request.
 * PREVIEW-FIRST: before spending a credit, ORBIT reads the request and shows its interpretation
 * (which source, which script mode, what it will say, which voice, how many). The admin confirms
 * that reading, THEN generates — so a misread is caught before a batch of wrong videos, not after.
 */
export function OrbitModal({ requestId, requestTitle, onClose, onStarted }:
  { requestId: string; requestTitle?: string; onClose: () => void; onStarted: () => void }) {
  const [ctx, setCtx] = useState('');
  const [interp, setInterp] = useState<Interpretation | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState('');
  const [seconds, setSeconds] = useState<number | ''>('');
  const [captions, setCaptions] = useState(true);
  // Which interpreted jobs the admin will actually generate, tracked by stable job index.
  // Defaulted to ALL jobs on every fresh preview (see effect below) so default = unchanged behaviour.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // Admin override for what the N variations differ by. undefined = use the backend's default axis.
  const [axis, setAxis] = useState<string | undefined>(undefined);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Conversational confirm (Phase 2) ─────────────────────────────────────────────────────────
  // A draft Studio conversation seeded from this request. The brain STATES its read, we cross-check it,
  // and a correction re-reads the plan — nothing is generated until the Generate button (Phase 4).
  const [conv, setConv] = useState<ConvMsg[]>([]);
  const [convSending, setConvSending] = useState(false);
  const [corr, setCorr] = useState('');
  const convSeeded = useRef(false);
  const convEndRef = useRef<HTMLDivElement | null>(null);
  const corrRef = useRef<HTMLTextAreaElement | null>(null);   // the answer box — a clarifying question focuses it

  // Post one conversational turn to the DRAFT ORBIT thread, then reload it. Passes the current preview
  // read (intent/summary) so the stated flow matches the checklist below. Never generates/spends.
  const sendOrbit = async (opts: { message?: string; orbit_context?: string; confirm?: boolean }) => {
    setConvSending(true);
    try {
      await regenApi.orbitChat(requestId, { ...opts, intent: interp?.intent, summary: interp?.summary });
      const t = await regenApi.studioThread(`orbit:${requestId}`);
      setConv((t.data?.messages || []) as ConvMsg[]);
    } catch (_) { /* conversation is advisory — a failure never blocks the checklist/Generate */ }
    finally { setConvSending(false); }
  };
  // Confirm the read (locks it in the conversation; does NOT dispatch — Generate does that).
  const confirmRead = () => sendOrbit({ confirm: true });
  // A correction / answer → fold into Extra direction, re-read the plan, AND log the turn.
  const sendCorrection = (text: string) => {
    const t = text.trim();
    if (!t) return;
    const next = ctx.trim() ? `${ctx.replace(/\s+$/, '')}\n${t}` : t;
    setCtx(next);
    setCorr('');
    schedule(next, true);                       // re-read: updates intent + the job checklist (the plan)
    sendOrbit({ message: t, orbit_context: next });   // running conversation: the brain re-states its read
  };

  // PREVIEW — spends nothing. Renders ORBIT's interpretation for confirmation.
  // axisArg (when given) overrides the axis state to dodge stale-closure timing on a fresh pick.
  const doPreview = async (context: string, axisArg?: string) => {
    setLoading(true); setErr('');
    try {
      const r = await regenApi.orbitGenerate(requestId, {
        orbit_context: context || undefined,
        captions,
        seconds: seconds || undefined,
        variation_axis: axisArg ?? axis,
        preview: true,
      });
      setInterp(r.data.interpretation || { jobs: [], unresolved: [] });
    } catch (e: any) {
      // "could not read a work order" comes back as a 400 — that's guidance, not a hard error.
      if (e?.response?.status === 400) {
        setInterp({ jobs: [], unresolved: [] });
      } else {
        setErr(e?.response?.data?.error || e.message || 'ORBIT could not read this request');
        setInterp(null);
      }
    } finally { setLoading(false); }
  };

  // Debounce re-reads so editing Extra direction / rapid axis clicks don't spam the endpoint.
  // Manual "Re-read" fires now.
  const schedule = (context: string, immediate = false, axisArg?: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (immediate) { doPreview(context, axisArg); return; }
    timerRef.current = setTimeout(() => doPreview(context, axisArg), 500);
  };

  // Admin picks a different axis → re-preview along it (debounced so rapid clicks don't stack).
  const onAxisChange = (value: string) => {
    if (value === (axis ?? interp?.variation_axis)) return;
    setAxis(value);
    schedule(ctx, false, value);
  };

  useEffect(() => {
    schedule('', true);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  // Seed the conversation ONCE, after the first preview resolves — so the brain's opening card is
  // stated with the same intent the checklist shows. Guarded by a ref so re-reads never re-seed.
  useEffect(() => {
    // only when the backend returns a brain read (Phase 1+) — otherwise no panel, so no draft thread.
    if (interp?.intent && !convSeeded.current) { convSeeded.current = true; sendOrbit({}); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interp]);

  // keep the transcript scrolled to the latest turn
  useEffect(() => { convEndRef.current?.scrollIntoView({ block: 'nearest' }); }, [conv]);

  // REAL GENERATE — same endpoint, no `preview`. Dispatches and hands off to the Team Room.
  const run = async () => {
    setRunning(true); setErr('');
    try {
      await regenApi.orbitGenerate(requestId, {
        orbit_context: ctx || undefined,
        seconds: seconds || undefined,
        captions,
        selected_jobs: Array.from(selected).sort((a, b) => a - b),
        variation_axis: axis ?? interp?.variation_axis,
        // Phase 4: pass the CONFIRMED read so dispatch honors the confirmed lane (server stays authoritative;
        // these are hints/overrides). gen_path comes from the chat's "Locked in — AI/avatar" confirmation.
        intent: interp?.intent,
        gen_path: ([...conv].reverse().find(m => m.role === 'assistant' && m.data?.orbit_confirmed)?.data?.gen_path) || undefined,
      });
      onStarted();          // parent navigates to the Creative Team Room
    } catch (e: any) {
      setErr(e?.response?.data?.error || e.message || 'ORBIT failed');
      setRunning(false);
    }
  };

  const jobs = interp?.jobs || [];
  const total = jobs.reduce((n, j) => n + (j.count || 1), 0);
  const meta = { vertical: interp?.vertical, state: interp?.state };

  // Conversation-panel derived state. `intent` is present only on Phase-1+ backends; when it's absent
  // the whole panel is skipped and the modal behaves exactly as before (fully additive).
  const intent = interp?.intent;
  const flow = flowFromIntent(intent);
  // latest assistant card carries the current clarifying set + whether the read is confirmed
  const lastAsst = [...conv].reverse().find(m => m.role === 'assistant' && m.data);
  const clarifying: { slot: string; q: string }[] = lastAsst?.data?.clarifying || [];
  const confirmed = !!lastAsst?.data?.orbit_confirmed;

  // Vary-by axis. Only shown once the backend signals it supports the contract (older reads hide it).
  const axisSupported = !!(interp && (interp.variation_axis || interp.available_axes?.length));
  const axisOptions = interp?.available_axes?.length ? interp.available_axes : AXES;
  const activeAxis = axis ?? interp?.variation_axis ?? axisOptions[0];

  // Stable index for a job — backend-provided when present, array position as a resilient fallback.
  const jobIndex = (j: PreviewJob, i: number) => (typeof j.index === 'number' ? j.index : i);

  // On every fresh preview, default the selection to ALL jobs — so doing nothing generates all.
  useEffect(() => {
    setSelected(new Set(jobs.map(jobIndex)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interp]);

  const toggle = (idx: number) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    return next;
  });
  const allIndices = jobs.map(jobIndex);
  const allSelected = jobs.length > 0 && allIndices.every(idx => selected.has(idx));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIndices));

  // Button reflects the SELECTION: sum of count over selected jobs (matches how `total` is summed).
  const selectedTotal = jobs.reduce((n, j, i) => selected.has(jobIndex(j, i)) ? n + (j.count || 1) : n, 0);
  const selectedCount = jobs.reduce((n, j, i) => selected.has(jobIndex(j, i)) ? n + 1 : n, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        onClick={e => e.stopPropagation()}>
        {/* header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-500" /> Generate with ORBIT
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {requestTitle ? `“${requestTitle}”` : 'Auto-fulfil this request'} — delivered into this request, marked as ORBIT.
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 max-h-[65vh] overflow-y-auto">
          {/* ── CONVERSATIONAL CONFIRM (Phase 2) ────────────────────────────────────────────────
              A Studio-style read → cross-check → correct loop, layered ABOVE the job checklist.
              Only shown when the backend returns intent (Phase-1+); otherwise the modal is unchanged. */}
          {intent && (
            <div className="rounded-xl border border-violet-200 dark:border-violet-900/50 bg-violet-50/40 dark:bg-violet-950/10 p-3 space-y-3">
              {/* the FLOW + the brain's plain-language read */}
              <div className="flex items-start gap-2">
                <span className="shrink-0 mt-0.5 w-7 h-7 rounded-lg bg-violet-600 text-white flex items-center justify-center">
                  <MessageSquare className="w-4 h-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{flow.label}</span>
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 font-medium">
                      {intent.lane_label}
                    </span>
                    {intent.confidence && (
                      <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${CONF_BADGE[intent.confidence] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}>
                        {intent.confidence} confidence
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{flow.blurb}</div>
                  {interp?.summary && (
                    <div className="mt-1.5 text-sm text-gray-700 dark:text-gray-200">{interp.summary}</div>
                  )}
                </div>
              </div>

              {/* running conversation — the brain's cross-check card(s) + your replies */}
              {conv.length > 0 && (
                <div className="space-y-1.5 max-h-52 overflow-y-auto rounded-lg bg-white/60 dark:bg-gray-900/40 p-2 border border-violet-100 dark:border-violet-900/40">
                  {conv.map(m => (
                    <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-xs whitespace-pre-line ${m.role === 'user'
                        ? 'bg-violet-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'}`}>
                        {renderBold(m.text || '')}
                      </div>
                    </div>
                  ))}
                  {convSending && (
                    <div className="flex justify-start">
                      <div className="rounded-lg px-2.5 py-1.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> reading…
                      </div>
                    </div>
                  )}
                  <div ref={convEndRef} />
                </div>
              )}

              {/* clarifying question set — only the slots ORBIT is still missing / unsure about.
                  Tapping one JUMPS to the answer box (it does NOT put the question text in as your answer —
                  that was the old bug: clicking "what's the offer?" filled your reply with "what's the offer?"). */}
              {clarifying.length > 0 && !confirmed && (
                <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/70 dark:bg-amber-950/10 p-2 space-y-1">
                  <div className="flex items-center gap-1 text-[11px] font-semibold text-amber-800 dark:text-amber-300">
                    <HelpCircle className="w-3 h-3" /> A few things I need to confirm — answer below
                  </div>
                  {clarifying.map((c, i) => (
                    <button key={i} onClick={() => corrRef.current?.focus()} disabled={convSending}
                      className="block w-full text-left text-xs text-amber-900 dark:text-amber-200 hover:underline disabled:opacity-40">
                      • {c.q}
                    </button>
                  ))}
                </div>
              )}

              {/* cross-check: Confirm the read, or type a correction / answer */}
              {confirmed ? (
                <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                  <Check className="w-4 h-4" /> Read confirmed — hit Generate below when you're ready.
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <button onClick={confirmRead} disabled={convSending || running}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1">
                      <Check className="w-3.5 h-3.5" /> Confirm this read
                    </button>
                    <span className="text-[11px] text-gray-400">or correct it below — I'll re-read and update the plan</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <textarea ref={corrRef} value={corr} onChange={e => setCorr(e.target.value)} rows={1}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendCorrection(corr); } }}
                      placeholder={`e.g. "no — keep the original face", "use S2", "make it 3", "it's UGC + B-Roll"`}
                      className="flex-1 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent p-2 text-sm text-gray-900 dark:text-white resize-none" />
                    <button onClick={() => sendCorrection(corr)} disabled={convSending || !corr.trim()}
                      className="shrink-0 px-3 py-2 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40 flex items-center gap-1">
                      <Send className="w-3.5 h-3.5" /> Send
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* what ORBIT read — its interpretation, confirmed before any credits are spent */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
                What ORBIT read from this request
              </div>
              <button onClick={() => schedule(ctx, true)} disabled={loading || running}
                className="text-xs text-violet-600 hover:text-violet-700 disabled:opacity-40 flex items-center gap-1">
                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Re-read
              </button>
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                <Loader2 className="w-4 h-4 animate-spin" /> ORBIT is reading this request…
              </div>
            ) : err && !interp ? (
              <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/20 p-3 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div>{err}</div>
                  <button onClick={() => schedule(ctx, true)} className="mt-2 text-xs font-medium underline">
                    Try again
                  </button>
                </div>
              </div>
            ) : !jobs.length ? (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 text-sm text-amber-800 dark:text-amber-300">
                I couldn't tell what to make. In the box below, type the words they should say
                (“3 videos in S2”) or the name of a video to copy (“1 of S3-TX-OLD-LADY-RIT-1.mp4”).
              </div>
            ) : (
              <div className="space-y-2">
                {/* Vary-by axis — what the N variations differ by; picking one re-plans the jobs. */}
                {axisSupported && (
                  <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 p-2.5">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Vary by</span>
                    <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-700 divide-x divide-gray-300 dark:divide-gray-700 overflow-hidden">
                      {axisOptions.map(ax => (
                        <button key={ax} onClick={() => onAxisChange(ax)} disabled={loading || running}
                          className={`px-2.5 py-1 text-xs font-medium capitalize transition-colors disabled:opacity-40 ${ax === activeAxis
                            ? 'bg-violet-600 text-white'
                            : 'bg-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
                          {ax}
                        </button>
                      ))}
                    </div>
                    <span className="text-[11px] text-gray-400">what the {total} variation{total === 1 ? '' : 's'} differ by</span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs text-gray-500">
                    ORBIT will make <b>{total}</b> variation{total === 1 ? '' : 's'}:
                  </div>
                  {jobs.length > 1 && (
                    <button onClick={toggleAll} disabled={running}
                      className="text-xs text-violet-600 hover:text-violet-700 disabled:opacity-40 shrink-0">
                      {allSelected ? 'Select none' : 'Select all'}
                    </button>
                  )}
                </div>
                {jobs.map((j, i) => {
                  const idx = jobIndex(j, i);
                  const checked = selected.has(idx);
                  const label = j.source?.label || j.source?.filename || 'ORBIT-cast character';
                  const badge = MODE_BADGE[j.script_mode] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
                  const voice = j.voice && (j.voice.gender || j.voice.age_band)
                    ? [j.voice.gender, fmtAge(j.voice.age_band)].filter(Boolean).join(' · ')
                    : null;
                  return (
                    <div key={i} className={`flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3 ${checked ? '' : 'opacity-50'}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggle(idx)}
                        aria-label={`Generate ${label}`}
                        className="mt-1 shrink-0 w-4 h-4 accent-violet-600 cursor-pointer" />
                      <SourceThumb src={j.source} />
                      <div className="min-w-0 flex-1 text-sm">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className={`text-[11px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${badge}`}>
                            {j.script_mode || 'reuse'}
                          </span>
                          <span className="font-medium text-gray-900 dark:text-white truncate">{label}</span>
                          {/* Which of the N this card is, and along which axis — guarded for older reads. */}
                          {typeof j.variation_index === 'number' && typeof j.variation_total === 'number' && (
                            <span className="text-[11px] px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-semibold capitalize">
                              #{j.variation_index}/{j.variation_total}{j.variation_axis ? ` · ${j.variation_axis}` : ''}
                            </span>
                          )}
                          {j.count > 1 && (
                            <span className="text-[11px] px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 font-semibold">
                              {j.count}×
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-gray-600 dark:text-gray-300 break-words">
                          {j.script_preview}
                        </div>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                          {voice && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                              <Mic className="w-3 h-3" /> {voice}
                            </span>
                          )}
                          {j.state && (
                            <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">{j.state}</span>
                          )}
                          <span className={`px-1.5 py-0.5 rounded ${captions
                            ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'}`}>
                            captions {captions ? 'on' : 'off'}
                          </span>
                          {/* recast plan — distinct accent so it never blurs into the script_mode badge */}
                          {j.recast?.enabled && (j.recast.plan === 'generate' ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-300 font-medium">
                              <Sparkles className="w-3 h-3" /> recast → generate new avatar
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300 font-medium">
                              <RefreshCw className="w-3 h-3" /> recast → library{j.recast.match ? `: ${j.recast.match}` : ''}
                            </span>
                          ))}
                          {j.multi_face && j.multi_face.count >= 2 && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 font-medium">
                              <AlertTriangle className="w-3 h-3" /> {j.multi_face.count} people in clip — one voice used for all
                            </span>
                          )}
                        </div>
                        {j.recast?.enabled && j.recast.plan === 'generate' && (
                          <div className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                            Generates a fresh face (Nano Banana + Seedance) — costs a bit more and takes longer.
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div className="text-xs text-gray-500">
                  → <b>{total}</b> video{total === 1 ? '' : 's'}
                  {meta.vertical ? ` · ${meta.vertical}` : ''}{meta.state ? ` · ${meta.state}` : ''}
                </div>

                {/* samples/refs ORBIT couldn't map to a job — subtle, so nothing silently drops */}
                {interp && interp.unresolved.length > 0 && (
                  <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/60 dark:bg-amber-950/10 p-2.5 text-xs text-amber-800 dark:text-amber-300 space-y-0.5">
                    {interp.unresolved.map((u, i) => (
                      <div key={i} className="flex items-start gap-1.5">
                        <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                        <span>couldn't map: <span className="font-mono">{u.filename || '(unnamed)'}</span> — {u.reason}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* extra context */}
          <div>
            <div className="flex items-center justify-between gap-2">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Extra direction <span className="font-normal text-gray-400">(optional — the offer, the angle, anything the brief misses)</span>
              </label>
              <TemplateLibrary
                onInsert={(text) => {
                  const next = ctx.trim() ? `${ctx.replace(/\s+$/, '')}\n${text}` : text;
                  setCtx(next);
                  schedule(next);
                }}
              />
            </div>
            <textarea value={ctx} onChange={e => { setCtx(e.target.value); schedule(e.target.value); }} rows={2}
              placeholder='e.g. "state the $29/month offer, keep it urgent"'
              className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent p-2 text-sm text-gray-900 dark:text-white" />
          </div>

          <div className="flex items-center gap-4 text-sm">
            <label className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <input type="checkbox" checked={captions} onChange={e => setCaptions(e.target.checked)} />
              burn captions
            </label>
            <label className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
              length
              <input type="number" min={6} max={90} value={seconds}
                onChange={e => setSeconds(e.target.value ? parseInt(e.target.value, 10) : '')}
                placeholder="auto"
                className="w-20 rounded border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1" />
              <span className="text-gray-400 text-xs">s — blank = fit the script</span>
            </label>
          </div>

          {err && jobs.length > 0 && <div className="text-sm text-red-600">⚠ {err}</div>}
        </div>

        {/* footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <span className="text-xs text-gray-500">Lands in this request → QC, credited to ORBIT.</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
              Cancel
            </button>
            <button onClick={run} disabled={running || loading || !jobs.length || selectedCount === 0}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 flex items-center gap-2">
              {running ? <><Loader2 className="w-4 h-4 animate-spin" /> starting…</>
                : <><Sparkles className="w-4 h-4" /> Make {selectedTotal || ''} {selectedTotal === 1 ? 'video' : 'videos'}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrbitModal;
